import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import { Refresh } from "@material-ui/icons";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import React, { useCallback, useEffect, useState } from "react";
import { getMeetingLinksDashboard } from "../service";
import { safeDateFormat } from "../utils";
import { openExternalUrl } from "../urlSafety";

// The hearing's own date and time, as the backend sends them. meeting_datetime
// carries a UTC offset, so formatting it in the browser shifts the time (11:00 AM
// showed as 4:30 PM in IST); meeting_time is already the time to show.
const meetingWhen = (meeting) => {
  if (meeting?.meeting_time) {
    const date = meeting.listing_date
      ? safeDateFormat(meeting.listing_date)
      : safeDateFormat(String(meeting.meeting_datetime || "").slice(0, 10));
    return `${date}, ${meeting.meeting_time}`;
  }
  return safeDateFormat(meeting?.meeting_datetime, "dd/MM/yyyy, hh:mm a");
};

// ---------------------------------------------------------------------------
// API fetcher
// ---------------------------------------------------------------------------
const fetchMeetingLinks = async (search = "") => {
  const params = {};
  if (search) {
    params.search = search;
  }
  const response = await getMeetingLinksDashboard(params);
  return response.data;
};

// ---------------------------------------------------------------------------
// Status chip styling
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  upcoming: { bg: "#FDF2CF", color: TEXT.warning, label: "Upcoming" },
  live: { bg: "#E4F5EA", color: TEXT.success, label: "Live" },
  completed: { bg: "#F2F4F7", color: TEXT.secondary, label: "Completed" },
  cancelled: { bg: "#FDE7E7", color: TEXT.error, label: "Cancelled" },
};
const DEFAULT_STATUS_STYLE = { bg: "#F2F4F7", color: TEXT.secondary, label: "Unknown" };



// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  container: {
    textAlign: "left",
  },
  helperText: {
    fontSize: "0.875rem",
    color: TEXT.muted,
    marginBottom: theme.spacing(2),
  },
  tableHeadRow: {
    backgroundColor: "#f5f5f5",
    "& th": {
      padding: "10px",
      fontWeight: 600,
      fontSize: "0.875rem",
    },
  },
  tableBodyCell: {
    padding: "10px",
    fontSize: "0.875rem",
  },
  caseTitleCell: {
    padding: "10px",
    fontSize: "0.875rem",
    minWidth: "220px",
    maxWidth: "320px",
    wordBreak: "break-word",
    whiteSpace: "normal",
  },
  joinButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8rem",
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function MeetingLinks() {
  const classes = useStyles();

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchVal, setSearchVal] = useState("");

  const loadMeetings = useCallback(async (searchQuery = "") => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchMeetingLinks(searchQuery);
      // The API may return an array directly or an object with a results/data key.
      const list = Array.isArray(data)
        ? data
        : data?.results || data?.data || [];
      setMeetings(list);
    } catch (err) {
      console.error("Failed to load meeting links:", err);
      const data = err?.response?.data;
      setError(
        data?.message ||
          data?.error ||
          data?.detail ||
          "Could not load meeting links. Please try again."
      );
    }
    setLoading(false);
  }, []);

  // Debounced API search on typing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadMeetings(searchVal);
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchVal, loadMeetings]);

  return (
    <Box className={classes.container}>

      <Typography className={classes.helperText}>
        View and join scheduled meetings for all cases.
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box maxWidth={360} flexGrow={1}>
          <TextField
            label="Search by case number or title"
            variant="outlined"
            size="small"
            fullWidth
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
          />
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => loadMeetings(searchVal)} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Typography color="error" style={{ marginBottom: 16 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box p={4}>
          <CircularCenteredLoader />
        </Box>
      ) : (
        <Paper
          style={{
            width: "100%",
            overflowX: "auto",
            boxShadow: "none",
            border: "1px solid #ddd",
          }}
        >
          <TableContainer>
            <Table size="small" style={{ maxWidth: "100%", marginTop: 0 }}>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell>S.No.</TableCell>
                  <TableCell>Case No.</TableCell>
                  <TableCell>Case Title</TableCell>
                  <TableCell>Notice Date</TableCell>
                  <TableCell>Meeting Date</TableCell>
                  <TableCell align="center">Join</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {meetings.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      style={{ padding: "2rem" }}
                    >
                      <Typography color="textSecondary">
                        No meetings found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  meetings.map((meeting, idx) => {
                    const statusStyle =
                      STATUS_STYLES[meeting.status] || DEFAULT_STATUS_STYLE;
                    const canJoin =
                      meeting.meeting_link &&
                      (meeting.status === "upcoming" ||
                        meeting.status === "live");

                    return (
                      <TableRow key={meeting.id || idx} hover>
                        <TableCell className={classes.tableBodyCell}>
                          {idx + 1}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {meeting.case_number || "—"}
                        </TableCell>
                        <TableCell className={classes.caseTitleCell}>
                          {meeting.case_title || "—"}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {safeDateFormat(meeting.notice_date)}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {meetingWhen(meeting)}
                        </TableCell>
                        <TableCell
                          className={classes.tableBodyCell}
                          align="center"
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            className={classes.joinButton}
                            disabled={!canJoin}
                            style={{ whiteSpace: "nowrap" }}
                            onClick={() => {
                              if (meeting.meeting_link) {
                                openExternalUrl(meeting.meeting_link, {
                                  context: "meeting link",
                                  onError: setError,
                                });
                              }
                            }}
                          >
                            Click to Join
                          </Button>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          <Chip
                            label={statusStyle.label}
                            size="small"
                            style={{
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.color,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
